import type { Item } from "../api";

export type ItemSortOption =
    | "expiry-asc"
    | "expiry-desc"
    | "name-asc"
    | "name-desc"
    | "quantity-desc"
    | "quantity-asc"
    | "zone-asc"
    | "added-desc";

export const DEFAULT_ITEM_SORT: ItemSortOption = "expiry-asc";

export const ITEM_SORT_LABELS: Record<ItemSortOption, string> = {
    "expiry-asc": "Expiry (soonest)",
    "expiry-desc": "Expiry (latest)",
    "name-asc": "Name (A–Z)",
    "name-desc": "Name (Z–A)",
    "quantity-desc": "Quantity (high)",
    "quantity-asc": "Quantity (low)",
    "zone-asc": "Zone (A–Z)",
    "added-desc": "Recently added",
};

function compareName(a: Item, b: Item): number {
    return a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
}

function compareExpiryAsc(a: Item, b: Item): number {
    if (!a.expiry_date && !b.expiry_date) return compareName(a, b);
    if (!a.expiry_date) return 1;
    if (!b.expiry_date) return -1;
    return a.expiry_date.localeCompare(b.expiry_date) || compareName(a, b);
}

function compareBySort(a: Item, b: Item, sortBy: ItemSortOption): number {
    switch (sortBy) {
        case "expiry-asc":
            return compareExpiryAsc(a, b);
        case "expiry-desc": {
            if (!a.expiry_date && !b.expiry_date) return compareName(a, b);
            if (!a.expiry_date) return 1;
            if (!b.expiry_date) return -1;
            return b.expiry_date.localeCompare(a.expiry_date) || compareName(a, b);
        }
        case "name-asc":
            return compareName(a, b);
        case "name-desc":
            return compareName(b, a);
        case "quantity-desc":
            return b.quantity - a.quantity || compareName(a, b);
        case "quantity-asc":
            return a.quantity - b.quantity || compareName(a, b);
        case "zone-asc":
            return (
                (a.location_name ?? "").localeCompare(b.location_name ?? "", undefined, {
                    sensitivity: "base",
                }) || compareName(a, b)
            );
        case "added-desc":
            return (b.created_at ?? "").localeCompare(a.created_at ?? "") || b.id - a.id;
        default:
            return compareExpiryAsc(a, b);
    }
}

function tokenScore(item: Item, token: string): number {
    const barcode = item.barcode?.toLowerCase();
    if (barcode) {
        if (barcode === token) return 100;
        if (barcode.startsWith(token)) return 80;
        if (barcode.includes(token)) return 60;
    }

    const name = item.name.toLowerCase();
    if (name === token) return 90;
    if (name.startsWith(token)) return 60;
    if (name.includes(token)) return 40;

    const fields = [item.brand, item.category, item.notes, item.location_name];
    for (const f of fields) {
        if (!f) continue;
        const v = f.toLowerCase();
        if (v.startsWith(token)) return 25;
        if (v.includes(token)) return 15;
    }
    return 0;
}

function searchScore(item: Item, query: string): number {
    const tokens = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (tokens.length === 0) return 0;
    return tokens.reduce((sum, token) => sum + tokenScore(item, token), 0);
}

export function sortItems(items: Item[], sortBy: ItemSortOption, query?: string): Item[] {
    const sorted = [...items];

    if (query?.trim()) {
        const normalized = query.trim().toLowerCase();
        sorted.sort((a, b) => {
            const scoreA = searchScore(a, normalized);
            const scoreB = searchScore(b, normalized);
            if (scoreB !== scoreA) return scoreB - scoreA;
            return compareBySort(a, b, sortBy);
        });
        return sorted;
    }

    sorted.sort((a, b) => compareBySort(a, b, sortBy));
    return sorted;
}
